if (!customElements.get('facet-form')) {
  customElements.define(
    'facet-form',
    class FacetForm extends HTMLFormElement {
      constructor() {
        super();

        this.dirty = false;
        this.cachedMap = new Map();

        this.addEventListener('change', this.onFormChange);
        this.addEventListener('submit', this.onFormSubmit);
      }

      onFormChange() {
        this.dirty = true;
        this.dispatchEvent(new Event('submit', { cancelable: true }));
      }

      onFormSubmit(event) {
        event.preventDefault();
        if (!this.dirty) return;

        const url = this.buildUrl().toString();
        this.renderSection(url, event);
      }

      buildUrl() {
        const searchParams = new URLSearchParams(new FormData(this));
        const url = new URL(this.action);

        url.search = '';
        searchParams.forEach((value, key) => url.searchParams.append(key, value));

        ['page', 'filter.v.price.gte', 'filter.v.price.lte'].forEach((item) => {
          if (url.searchParams.get(item) === '') {
            url.searchParams.delete(item);
          }
        });

        url.searchParams.set('section_id', theme.utils.sectionId(this));
        return url;
      }

      updateURLHash(url) {
        const clonedUrl = new URL(url);
        clonedUrl.searchParams.delete('section_id');
        history.replaceState({}, '', clonedUrl.toString());
      }

      beforeRenderSection() {
        const container = document.getElementById('ProductGridContainer');
        const items = container.querySelectorAll('.product-card');
        const translateY = theme.config.motionReduced ? 0 : 50;

        Motion.timeline([
          [items, { y: translateY, opacity: 0, visibility: 'hidden' }, { duration: 0.5, delay: theme.config.motionReduced ? 0 : Motion.stagger(0.1) }],
          [container, { y: translateY, opacity: 0 }, { duration: 0.5, easing: 'linear' }],
        ]);

        setTimeout(() => {
          const target = document.querySelector('.collection');
          window.scrollTo({
            top: target.getBoundingClientRect().top + window.scrollY - (theme.config.isTouch || theme.config.mqlSmall ? 95 : 20),
            behavior: theme.config.motionReduced ? 'auto' : 'smooth'
          });

          const drawer = document.getElementById('FacetDrawer');
          if (drawer) drawer.classList.add('loading');
        }, 100);
      }

      afterRenderSection() {
        const container = document.getElementById('ProductGridContainer');
        const items = container.querySelectorAll('.product-card');
        const translateY = theme.config.motionReduced ? 0 : 50;

        Motion.timeline([
          [container, { y: [translateY, 0], opacity: [0, 1] }, { duration: 0.5, easing: 'linear' }],
          [items, { y: [translateY, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] }, { duration: 0.5, delay: theme.config.motionReduced ? 0 : Motion.stagger(0.1) }],
        ]);

        const drawer = document.getElementById('FacetDrawer');
        if (drawer) drawer.classList.remove('loading');

        document.dispatchEvent(new CustomEvent('collection:reloaded'));
      }

      renderSection(url, event) {
        this.cachedMap.has(url)
          ? this.renderSectionFromCache(url, event)
          : this.renderSectionFromFetch(url, event);

        if (this.hasAttribute('data-history')) this.updateURLHash(url);

        this.dirty = false;
      }

      renderSectionFromFetch(url, event) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        
        this.beforeRenderSection();
        const start = performance.now();

        fetch(url, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            const execution = (performance.now() - start);

            setTimeout(() => {
              this.renderFilters(responseText, event);
              this.renderFiltersActive(responseText);
              this.renderProductGridContainer(responseText);
              this.renderProductCount(responseText);
              this.renderSortBy(responseText);

              theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.facetUpdate, { responseText: responseText });
              this.cachedMap.set(url, responseText);

              this.afterRenderSection();
            }, execution > 500 ? 0 : 500);
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            }
            else {
              console.error(error);
            }
          });
      }

      renderSectionFromCache(url, event) {
        this.beforeRenderSection();

        setTimeout(() => {
          const responseText = this.cachedMap.get(url);
          this.renderFilters(responseText, event);
          this.renderFiltersActive(responseText);
          this.renderProductGridContainer(responseText);
          this.renderProductCount(responseText);
          this.renderSortBy(responseText);

          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.facetUpdate, { responseText: responseText });

          this.afterRenderSection();
        }, 500);
      }

      renderFilters(responseText, event) {
        const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
        const facetElements = parsedHTML.querySelectorAll(
          '#FacetFiltersContainer [data-filter], #MobileFacetFiltersContainer [data-filter]'
        );

        const matchesIndex = (element) => {
          const jsFilter = event ? event.target.closest('[data-filter]') : undefined;
          return jsFilter ? element.getAttribute('data-index') === jsFilter.getAttribute('data-index') : false;
        };
        const facetsToRender = Array.from(facetElements).filter((element) => !matchesIndex(element));

        facetsToRender.forEach((element) => {
          const filter = document.querySelector(`[data-filter][data-index="${element.getAttribute('data-index')}"]`);
          if (filter !== null) {
            if (filter.tagName === 'DETAILS') {
              filter.querySelector('summary + *').innerHTML = element.querySelector('summary + *').innerHTML;
            }
            else {
              filter.innerHTML = element.innerHTML;
            }
          }
        });
      }

      renderFiltersActive(responseText) {
        const id = 'FacetFiltersActive';
        if (document.getElementById(id) === null) return;
        const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

        document.getElementById(id).innerHTML = parsedHTML.getElementById(id).innerHTML;
      }

      renderProductGridContainer(responseText) {
        const id = 'ProductGridContainer';
        if (document.getElementById(id) === null) return;
        const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
        parsedHTML.querySelector('motion-list').setAttribute('motion-reduced', '');

        document.getElementById(id).innerHTML = parsedHTML.getElementById(id).innerHTML;
      }

      renderProductCount(responseText) {
        const id = 'ProductCount';
        if (document.getElementById(id) === null) return;
        const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

        document.getElementById(id).innerHTML = parsedHTML.getElementById(id).innerHTML;
      }

      renderSortBy(responseText) {
        const id = 'SortByContainer';
        if (document.getElementById(id) === null) return;
        const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
        const sortContainer = parsedHTML.getElementById(id);

        if (sortContainer === null) return;

        document.getElementById(id).innerHTML = sortContainer.innerHTML;
      }
    }, { extends: 'form' }
  );
}

if (!customElements.get('facet-count')) {
  customElements.define(
    'facet-count',
    class FacetCount extends HTMLElement {
      constructor() {
        super();
      }

      facetUpdateUnsubscriber = undefined;

      connectedCallback() {
        this.facetUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.facetUpdate, this.onFacetUpdate.bind(this));
      }

      disconnectedCallback() {
        if (this.facetUpdateUnsubscriber) {
          this.facetUpdateUnsubscriber();
        }
      }

      get itemCount() {
        return parseInt(this.innerText);
      }

      onFacetUpdate(event) {
        const parsedHTML = new DOMParser().parseFromString(event.responseText, 'text/html');
        const facetCount = parsedHTML.querySelector('facet-count');
        this.innerText = facetCount.innerHTML;
        this.hidden = this.itemCount === 0;
      }
    }
  );
}

if (!customElements.get('facet-remove')) {
  customElements.define(
    'facet-remove',
    class FacetRemove extends MagnetLink {
      constructor() {
        super();

        this.addEventListener('click', this.onClick);
      }

      onClick(event) {
        const form = this.closest('form[is="facet-form"]') || document.querySelector('form[is="facet-form"]');

        if (form) {
          event.preventDefault();

          const url = new URL(this.href);
          url.searchParams.set('section_id', theme.utils.sectionId(form));
          form.renderSection(url.toString(), event);
        }
      }
    }, { extends: 'a' }
  );
}

if (!customElements.get('facet-sort')) {
  customElements.define(
    'facet-sort',
    class FacetSort extends HTMLElement {
      constructor() {
        super();

        this.lastKnownSelection = '';
        this.onCollectionReloaded = this.onCollectionReloaded.bind(this);

        Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });

        this.addEventListener('change', this.onChange);
        if (this.button) {
          this.button.addEventListener('click', this.show.bind(this));
        }
        if (this.close) {
          this.close.addEventListener('click', this.hide.bind(this));
        }
        document.addEventListener('click', this.onWindowClick.bind(this));
        document.addEventListener('collection:reloaded', this.onCollectionReloaded);
      }

      disconnectedCallback() {
        document.removeEventListener('collection:reloaded', this.onCollectionReloaded);
      }

      get listbox() {
        return this.querySelector('.sort-listbox');
      }

      get selection() {
        return this.querySelector('.sort-selection');
      }

      get button() {
        return this.querySelector('.sort-by');
      }

      get close() {
        return this.querySelector('.sort-close');
      }

      init() {
        if (!this.selection || !this.listbox) return;
        if (this.button) this.button.removeAttribute('open');
        this.captureSelectionSnapshot();
        this.syncSelectionFromChecked();
        this.syncSelectionFromUrl();
        this.syncSelectionFromNativeSelect();
        this.ensureSelectionText();
        this.initButton();
        this.style.setProperty('--facet-listbox-height', `${this.listbox.getBoundingClientRect().height}px`);
      }

      initButton() {
        if (!this.selection) return;
        const value = (this.selection?.innerText || '').trim() || this.lastKnownSelection;
        const width = theme.getElementWidth(this.selection, value);
        this.style.setProperty('--facet-button-width', `${Math.max(width, 80)}px`);
      }

      onCollectionReloaded() {
        if (this.button) this.button.removeAttribute('open');
        this.ensureSelectionText();
        this.initButton();
      }

      captureSelectionSnapshot() {
        const value = (this.selection?.innerText || '').trim();
        if (value.length > 0) {
          this.lastKnownSelection = value;
        }
      }

      syncSelectionFromChecked() {
        const checkedInput = this.querySelector('.sort-listbox input[name="sort_by"]:checked');
        if (!checkedInput) return;

        const label = this.querySelector(`label[for="${checkedInput.id}"]`);
        if (!label) return;

        const value = (label.innerText || '').trim();
        if (value.length === 0) return;

        this.selection.innerText = value;
        this.lastKnownSelection = value;
      }

      syncSelectionFromUrl() {
        const sortBy = new URL(window.location.href).searchParams.get('sort_by');
        if (!sortBy) return;

        const value = this.getLabelFromSortValue(sortBy) || this.getFrenchSortLabel(sortBy) || sortBy;
        if (value.length === 0) return;

        this.selection.innerText = value;
        this.lastKnownSelection = value;
      }

      getLabelFromSortValue(sortBy) {
        if (!sortBy) return '';

        const sortInputs = this.querySelectorAll('.sort-listbox input[name="sort_by"]');
        let labelFromListbox = '';
        sortInputs.forEach((input) => {
          if (labelFromListbox.length > 0) return;
          if (input.value !== sortBy) return;

          const label = this.querySelector(`label[for="${input.id}"]`);
          const value = (label?.innerText || '').trim();
          if (value.length > 0) {
            labelFromListbox = value;
          }
        });
        if (labelFromListbox.length > 0) return labelFromListbox;

        const nativeOptions = document.querySelectorAll('form[is="facet-form"] select[name="sort_by"] option');
        let labelFromSelect = '';
        nativeOptions.forEach((option) => {
          if (labelFromSelect.length > 0) return;
          if (option.value !== sortBy) return;

          const value = (option.innerText || '').trim();
          if (value.length > 0) {
            labelFromSelect = value;
          }
        });

        return labelFromSelect;
      }

      getFrenchSortLabel(sortBy) {
        if (!sortBy) return '';

        const labels = {
          'manual': 'En vedette',
          'best-selling': 'Meilleures ventes',
          'best_selling': 'Meilleures ventes',
          'title-ascending': 'Alphabétique, A à Z',
          'title_ascending': 'Alphabétique, A à Z',
          'title-descending': 'Alphabétique, Z à A',
          'title_descending': 'Alphabétique, Z à A',
          'price-ascending': 'Prix, croissant',
          'price_ascending': 'Prix, croissant',
          'price-descending': 'Prix, décroissant',
          'price_descending': 'Prix, décroissant',
          'created-ascending': 'Date, ancienne à récente',
          'created_ascending': 'Date, ancienne à récente',
          'created-descending': 'Date, récente à ancienne',
          'created_descending': 'Date, récente à ancienne',
          'relevance': 'Pertinence'
        };

        return labels[sortBy] || '';
      }

      syncSelectionFromNativeSelect() {
        const selectedOption = document.querySelector('form[is="facet-form"] select[name="sort_by"] option:checked');
        const value = (selectedOption?.innerText || '').trim();
        if (value.length === 0) return;

        this.selection.innerText = value;
        this.lastKnownSelection = value;
      }

      ensureSelectionText() {
        const currentValue = (this.selection?.innerText || '').trim();
        if (currentValue.length > 0) {
          this.lastKnownSelection = currentValue;
          return;
        }

        this.syncSelectionFromChecked();
        const checkedValue = (this.selection?.innerText || '').trim();
        if (checkedValue.length > 0) return;

        this.syncSelectionFromUrl();
        const urlValue = (this.selection?.innerText || '').trim();
        if (urlValue.length > 0) return;

        this.syncSelectionFromNativeSelect();
        const nativeSelectValue = (this.selection?.innerText || '').trim();
        if (nativeSelectValue.length > 0) return;

        if (this.lastKnownSelection.length > 0) {
          this.selection.innerText = this.lastKnownSelection;
          return;
        }

        const firstOptionLabel = this.querySelector('.sort-listbox label');
        const firstOptionValue = (firstOptionLabel?.innerText || '').trim();
        if (firstOptionValue.length > 0) {
          this.selection.innerText = firstOptionValue;
          this.lastKnownSelection = firstOptionValue;
          return;
        }

        const titleValue = (this.querySelector('.sort-title')?.innerText || '').trim();
        if (titleValue.length > 0) {
          this.selection.innerText = titleValue;
        }
      }

      onChange(event) {
        const target = event.target;
        if (!(target instanceof HTMLInputElement)) return;
        if (target.name !== 'sort_by' || target.type !== 'radio') return;

        const label = this.querySelector(`label[for="${target.id}"]`);
        const nextSelection = (label?.innerText || '').trim();

        const form = this.closest('form[is="facet-form"]') || document.querySelector('form[is="facet-form"]');
        const hasActiveXcloudFilter = Boolean(document.querySelector('.cloud-search-applied-filter'));

        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', target.value);
        url.searchParams.delete('page');

        if (form && !hasActiveXcloudFilter) {
          const sectionUrl = new URL(url.toString());
          sectionUrl.searchParams.set('section_id', theme.utils.sectionId(form));
          form.renderSection(sectionUrl.toString(), event);
        } else {
          window.location.assign(url.toString());
        }

        if (nextSelection.length > 0) {
          this.selection.innerText = nextSelection;
          this.lastKnownSelection = nextSelection;
        } else {
          this.syncSelectionFromChecked();
          this.syncSelectionFromUrl();
          this.ensureSelectionText();
        }
        this.initButton();
        this.hide();
      }

      show() {
        if (!this.button) return;
        this.button.setAttribute('open', '');
      }

      hide(immediate = true) {
        if (!this.button) return;
        if (this.button.hasAttribute('open')) {
          setTimeout(() => {
            this.button.removeAttribute('open');
          }, 100);

          if (theme.config.isTouch || document.body.getAttribute('data-button-hover') === 'none') return;
          
          const btnFill = this.button.querySelector('[data-fill');
          Motion.animate(btnFill, { y: ['0%', immediate ? '0%' : '-76%'] }, { duration: 0.6, delay: immediate ? 0 : 0.2 });
        }
      }

      onWindowClick(event) {
        if (!this.button) return;
        if (!this.contains(event.target)) {
          if (this.button.hasAttribute('open')) {
            this.hide(false);
          }
        }
      }
    }
  );
}

if (!customElements.get('facet-sticky')) {
    customElements.define(
        'facet-sticky',
        class FacetSticky extends HTMLElement {
            constructor() {
                super();

                new IntersectionObserver(this.handleIntersection.bind(this), {
                    rootMargin: `-${screen.availHeight - 100}px 0px ${screen.availHeight}px 0px`
                }).observe(document.querySelector('.collection-section'));
            }

            get button() {
                return this.querySelector('.button');
            }

            get filter() {
                return document.querySelector(
                    '.cloud-search-filter-mobile .cloud-search-page-controls__filters-link'
                );
            }

            handleIntersection(entries) {
                if (entries[0].isIntersecting) {
                    if (this.button) {
                        Motion.animate(this.button, {
                            opacity: 1,
                            visibility: 'visible',
                            transform: ['translateY(15px)', 'translateY(0)']
                        }, {
                            duration: 1,
                            easing: [0.16, 1, 0.3, 1]
                        });
                    }

                    if (this.filter) {
                        Motion.animate(this.filter, {
                            opacity: 1,
                            visibility: 'visible',
                            transform: ['translateY(15px)', 'translateY(0)']
                        }, {
                            duration: 1,
                            easing: [0.16, 1, 0.3, 1]
                        });
                    }
                } else {
                    if (this.button) {
                        Motion.animate(this.button, {
                            opacity: 0,
                            visibility: 'hidden',
                            transform: ['translateY(0)', 'translateY(15px)']
                        }, {
                            duration: 1,
                            easing: [0.16, 1, 0.3, 1]
                        });
                    }

                    if (this.filter) {
                        Motion.animate(this.filter, {
                            opacity: 0,
                            visibility: 'hidden',
                            transform: ['translateY(0)', 'translateY(15px)']
                        }, {
                            duration: 1,
                            easing: [0.16, 1, 0.3, 1]
                        });
                    }
                }
            }
        }
    );
}

const setupXcloudFiltersOverlay = () => {
  if (window.__xcloudFiltersOverlaySetup) return;
  if (!document.body) return;

  window.__xcloudFiltersOverlaySetup = true;

  const overlay = document.createElement('div');
  overlay.className = 'xcloud-filters-overlay';
  overlay.setAttribute('aria-hidden', 'true');
  overlay.textContent = ' ';
  document.body.appendChild(overlay);

  const isPaneOpen = () => {
    const pane = document.querySelector('.cloud-search-filters-pane');
    if (!pane) return false;

    const style = window.getComputedStyle(pane);
    const rect = pane.getBoundingClientRect();

    return style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      parseFloat(style.opacity || '1') > 0 &&
      rect.width > 0 &&
      rect.height > 0;
  };

  const syncOverlayState = () => {
    const isOpen = isPaneOpen();
    overlay.classList.toggle('active', isOpen);
  };

  const scheduleSync = () => {
    [0, 120, 300].forEach((delay) => setTimeout(syncOverlayState, delay));
  };

  overlay.addEventListener('click', () => {
    document.querySelector('.cloud-search-filters-pane__close')?.click();
    overlay.classList.remove('active');
  });

  document.addEventListener('click', (event) => {
    if (
      event.target.closest('.cloud-search-page-controls__filters-link') ||
      event.target.closest('.cloud-search-filters-pane__close') ||
      event.target.closest('.cloud-search-filter-value') ||
      event.target.closest('.cloud-search-applied-filter')
    ) {
      scheduleSync();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      scheduleSync();
    }
  });

  document.addEventListener('collection:reloaded', scheduleSync);
  window.addEventListener('resize', scheduleSync);

  scheduleSync();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupXcloudFiltersOverlay, { once: true });
} else {
  setupXcloudFiltersOverlay();
}

if (!customElements.get('price-range')) {
  customElements.define(
    'price-range',
    class PriceRange extends HTMLElement {
      constructor() {
        super();

        this.rangeMin = this.querySelector('input[type="range"]:first-child');
        this.rangeMax = this.querySelector('input[type="range"]:last-child');
        this.inputMin = this.querySelector('input[name="filter.v.price.gte"]');
        this.inputMax = this.querySelector('input[name="filter.v.price.lte"]');

        this.inputMin.addEventListener('focus', this.inputMin.select);
        this.inputMax.addEventListener('focus', this.inputMax.select);
        this.inputMin.addEventListener('change', this.onInputMinChange.bind(this));
        this.inputMax.addEventListener('change', this.onInputMaxChange.bind(this));

        this.rangeMin.addEventListener('change', this.onRangeMinChange.bind(this));
        this.rangeMax.addEventListener('change', this.onRangeMaxChange.bind(this));
        this.rangeMin.addEventListener('input', this.onRangeMinInput.bind(this));
        this.rangeMax.addEventListener('input', this.onRangeMaxInput.bind(this));
      }

      onInputMinChange(event) {
        event.preventDefault();
        event.target.value = Math.max(Math.min(parseInt(event.target.value), parseInt(this.inputMax.value || event.target.max) - 1), event.target.min);
        this.rangeMin.value = event.target.value;
        this.rangeMin.parentElement.style.setProperty('--range-min', `${parseInt(this.rangeMin.value) / parseInt(this.rangeMin.max) * 100}%`);
      }

      onInputMaxChange(event) {
        event.preventDefault();
        event.target.value = Math.min(Math.max(parseInt(event.target.value), parseInt(this.inputMin.value || event.target.min) + 1), event.target.max);
        this.rangeMax.value = event.target.value;
        this.rangeMax.parentElement.style.setProperty('--range-max', `${parseInt(this.rangeMax.value) / parseInt(this.rangeMax.max) * 100}%`);
      }

      onRangeMinChange(event) {
        event.stopPropagation();
        this.inputMin.value = event.target.value;
        this.inputMin.dispatchEvent(new Event('change', { bubbles: true }));
      }

      onRangeMaxChange(event) {
        event.stopPropagation();
        this.inputMax.value = event.target.value;
        this.inputMax.dispatchEvent(new Event('change', { bubbles: true }));
      }

      onRangeMinInput(event) {
        event.target.value = Math.min(parseInt(event.target.value), parseInt(this.inputMax.value || event.target.max) - 1);
        event.target.parentElement.style.setProperty('--range-min', `${parseInt(event.target.value) / parseInt(event.target.max) * 100}%`);
        this.inputMin.value = event.target.value;
      }

      onRangeMaxInput(event) {
        event.target.value = Math.max(parseInt(event.target.value), parseInt(this.inputMin.value || event.target.min) + 1);
        event.target.parentElement.style.setProperty('--range-max', `${parseInt(event.target.value) / parseInt(event.target.max) * 100}%`);
        this.inputMax.value = event.target.value;
      }
    }
  );
}

if (!customElements.get('infinite-button')) {
  customElements.define(
    'infinite-button',
    class InfiniteButton extends HoverButton {
      constructor() {
        super();

        this.onClickHandler = this.onClick.bind(this);
      }

      connectedCallback() {
        this.addEventListener('click', this.onClickHandler);

        if (this.getAttribute('mode') == 'infinite') {
          Motion.inView(this, this.onClickHandler, { margin: '200px 0px 200px 0px' });
        }
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClickHandler);
      }

      onClick() {
        if (this.hasAttribute('aria-busy')) return;

        this.enableLoading();
      }


      enableLoading() {
        this.classList.add('pointer-events-none');
        this.setAttribute('aria-busy', 'true');
      }
    }, { extends: 'button' }
  );
}
